import type { ReactNode } from "react";
import { ChevronRight, Search, type LucideIcon } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Skeleton,
} from "@lyder-talk/ui";
import { cn } from "@/lib/utils";
import { typeSubtitleClass } from "@/lib/typography";
import {
  sidebarDividerClass,
  sidebarNewButtonClass,
  sidebarRowActiveClass,
  sidebarRowClass,
  sidebarSectionHeaderClass,
} from "@/lib/card-styles";

/**
 * Shared sidebar shell primitives. Both the practice (My Topics) and AI
 * (My Topics AI) sidebars compose these so rows, section headers, the "New …"
 * action, search, loading, and empty states read identically across features.
 * Purely presentational — no data fetching lives here.
 */

/**
 * Panel title, sitting above the primary action and search.
 *
 * Deliberately NOT a `typeMetaClass` micro-label: the global rail already uses
 * that for its section labels, and a third grey caps row two columns over reads
 * as chrome furniture and disappears. This is the panel's own voice — a real
 * title over a hairline, so the list below has a stated subject instead of just
 * starting.
 *
 * Sticky: the topics panel scrolls its whole column (unlike the AI panel, which
 * only scrolls the list), so the title has to pin or it scrolls off the top.
 */
export const SidebarHeader = ({ title }: { title: string }) => (
  <div className={cn("sticky top-0 z-10 border-b bg-background px-3 pt-3.5 pb-2.5", sidebarDividerClass)}>
    <h2 className={cn(typeSubtitleClass, "truncate text-[15px]")}>{title}</h2>
  </div>
);

/** Gradient "New …" primary action. */
export const SidebarPrimaryAction = ({
  icon: Icon,
  label,
  onClick,
  "data-tour": dataTour,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  "data-tour"?: string;
}) => (
  <div className="px-3 py-2">
    <button onClick={onClick} className={sidebarNewButtonClass} data-tour={dataTour}>
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  </div>
);

/** Search field row. */
export const SidebarSearchField = ({
  value,
  onChange,
  placeholder = "Search...",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <div className="px-3 pb-2">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9 h-8 text-sm"
      />
    </div>
  </div>
);

/** Collapsible section with a shared chevron header. */
export const SidebarSection = ({
  title,
  open,
  onOpenChange,
  children,
  "data-tour": dataTour,
}: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  "data-tour"?: string;
}) => (
  <div className="px-2">
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <button className={sidebarSectionHeaderClass} data-tour={dataTour}>
          <span>{title}</span>
          <ChevronRight
            className={cn(
              "transition-transform duration-200 w-3.5 h-3.5 text-muted-foreground",
              open && "rotate-90",
            )}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="flex flex-col gap-0.5 pb-1">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  </div>
);

/**
 * Shared list row. The `actions` slot fades in on hover (or stays visible when
 * its trigger is open via data-[state=open]). Callers handle their own onClick.
 */
export const SidebarRow = ({
  active = false,
  onClick,
  children,
  actions,
  className,
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  actions?: ReactNode;
  className?: string;
}) => (
  <div
    onClick={onClick}
    className={cn(sidebarRowClass, active && sidebarRowActiveClass, className)}
  >
    <div className="flex-1 min-w-0">{children}</div>
    {actions && (
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 has-[[data-state=open]]:opacity-100 focus-within:opacity-100">
        {actions}
      </div>
    )}
  </div>
);

/**
 * Loading shimmer — mirrors the real shell: a couple of ghost section headers,
 * each with rows of varied widths (matching the row padding) so it reads as a
 * populating list rather than a block of bars.
 */
export const SidebarSkeleton = () => {
  const rowWidths = ["w-11/12", "w-3/4", "w-5/6", "w-2/3", "w-4/5"];
  return (
    <div className="flex flex-col gap-5 px-2 pt-3" aria-hidden>
      {[0, 1].map((section) => (
        <div key={section} className="flex flex-col gap-1">
          {/* Section header */}
          <div className="px-2 py-1.5">
            <Skeleton className="h-3.5 w-20" />
          </div>
          {/* Rows */}
          {[...Array(section === 0 ? 3 : 5)].map((_, i) => (
            <div key={i} className="px-2 py-1.5">
              <Skeleton
                className={`h-4 ${rowWidths[(i + section) % rowWidths.length]}`}
                style={{ animationDelay: `${(section * 5 + i) * 60}ms` }}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/** Centered empty state — icon + muted message. */
export const SidebarEmptyState = ({
  icon: Icon,
  message,
}: {
  icon: LucideIcon;
  message: string;
}) => (
  <div className="px-4 py-8 text-center text-muted-foreground text-sm">
    <Icon className="mx-auto mb-2 w-5 h-5 text-muted-foreground/50" />
    <p>{message}</p>
  </div>
);
